// ─────────────────────────────────────────────
//  CustomerReview — the page customers see
//  when they scan the QR code on their receipt
// ─────────────────────────────────────────────
import { useState } from "react";
import Stars from "../components/Stars";
import { PLATFORMS, QUICK_TAGS } from "../lib/data";

export default function CustomerReview({ business, onBack }) {
  const [step,      setStep]      = useState(0); // 0=rate, 1=write, 2=done
  const [stars,     setStars]     = useState(0);
  const [text,      setText]      = useState("");
  const [tags,      setTags]      = useState([]);
  const [platforms, setPlatforms] = useState(["google"]);
  const [loading,   setLoading]   = useState(false);

  // Review gating: 1–2 stars goes private, never public
  const isGated = stars > 0 && stars <= 2;

  const toggleTag      = (t) => setTags((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);
  const togglePlatform = (id) => setPlatforms((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const handleSubmit = () => {
    setLoading(true);
    // TODO: POST to Supabase here
    setTimeout(() => { setLoading(false); setStep(2); }, 1800);
  };

  const REACTION = ["", "😟 Sorry to hear that", "😐 We'd love to do better", "🙂 Thanks for the feedback", "😊 Glad you enjoyed it!", "🌟 You made our day!"];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f7f5f0",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 20,
      fontFamily: "inherit",
    }}>
      {/* Brand */}
      <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: 10, background: "linear-gradient(135deg,#FF6B35,#FFBE0B)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>⚡</div>
        <span style={{ fontWeight: 800, fontSize: 16, color: "#111", letterSpacing: "-0.03em" }}>JiffyReview</span>
      </div>

      <div style={{ width: "100%", maxWidth: 420, background: "white", borderRadius: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.1)", overflow: "hidden" }}>

        {/* Business header */}
        <div style={{ background: "linear-gradient(135deg,#FF6B35,#FFBE0B)", padding: "26px 26px 22px" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>{business.emoji}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "white", marginBottom: 3 }}>{business.name}</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>📍 {business.location} · {business.date}</div>
        </div>

        <div style={{ padding: "26px 26px 30px" }}>

          {/* ── STEP 0: Star rating ── */}
          {step === 0 && (
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#111", marginBottom: 4 }}>How was your visit?</div>
              <div style={{ fontSize: 14, color: "#999", marginBottom: 28 }}>Tap to rate your experience</div>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <Stars value={stars} onChange={setStars} size={52} />
              </div>
              <div style={{ textAlign: "center", fontSize: 15, fontWeight: 600, color: "#FF6B35", marginBottom: 30, minHeight: 24 }}>
                {REACTION[stars]}
              </div>
              <button
                onClick={() => stars > 0 && setStep(1)}
                disabled={!stars}
                style={{
                  width: "100%", padding: 15, borderRadius: 14, border: "none",
                  background: stars ? "linear-gradient(135deg,#FF6B35,#FFBE0B)" : "#f0f0f0",
                  color: stars ? "white" : "#bbb",
                  fontSize: 15, fontWeight: 700,
                  cursor: stars ? "pointer" : "not-allowed", fontFamily: "inherit",
                }}
              >
                Continue →
              </button>
            </div>
          )}

          {/* ── STEP 1 (gated): Private feedback for low stars ── */}
          {step === 1 && isGated && (
            <div>
              <div style={{ fontSize: 21, fontWeight: 800, color: "#111", marginBottom: 6 }}>We're sorry to hear that</div>
              <div style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>Your feedback helps us improve. Can you tell us more?</div>
              <Stars value={stars} size={20} />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What could we have done better?"
                style={{ width: "100%", minHeight: 110, marginTop: 14, background: "#f7f5f0", border: "1.5px solid #e8e4dc", borderRadius: 12, padding: 14, fontSize: 14, fontFamily: "inherit", resize: "none", outline: "none", color: "#333", boxSizing: "border-box" }}
              />
              <div style={{ background: "#fff8f0", border: "1px solid #ffe5cc", borderRadius: 12, padding: "14px 16px", marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#FF6B35", marginBottom: 6 }}>📧 We'll personally follow up</div>
                <div style={{ fontSize: 12, color: "#888" }}>Your feedback goes privately to the manager — not posted publicly.</div>
              </div>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{ width: "100%", padding: 14, borderRadius: 14, border: "none", marginTop: 18, background: loading ? "#f0f0f0" : "linear-gradient(135deg,#FF6B35,#FFBE0B)", color: loading ? "#bbb" : "white", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}
              >
                {loading ? "Sending…" : "Send Private Feedback"}
              </button>
            </div>
          )}

          {/* ── STEP 1 (public): Full review form ── */}
          {step === 1 && !isGated && (
            <div>
              <div style={{ fontSize: 21, fontWeight: 800, color: "#111", marginBottom: 4 }}>Tell us more</div>
              <div style={{ fontSize: 13, color: "#999", marginBottom: 18 }}>Optional — your words really help!</div>
              <Stars value={stars} size={20} />

              {/* Quick tags */}
              <div style={{ marginTop: 16, marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#aaa", marginBottom: 8, fontWeight: 500 }}>QUICK TAGS</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {QUICK_TAGS.map((t) => (
                    <button
                      key={t}
                      onClick={() => toggleTag(t)}
                      style={{
                        padding: "6px 12px", borderRadius: 20,
                        border: `1.5px solid ${tags.includes(t) ? "#FF6B35" : "#e8e4dc"}`,
                        background: tags.includes(t) ? "#fff3ee" : "white",
                        color: tags.includes(t) ? "#FF6B35" : "#888",
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share what made your experience great…"
                style={{ width: "100%", minHeight: 90, background: "#f7f5f0", border: "1.5px solid #e8e4dc", borderRadius: 12, padding: 14, fontSize: 14, fontFamily: "inherit", resize: "none", outline: "none", color: "#333", boxSizing: "border-box", marginBottom: 14 }}
              />

              {/* Platform picker */}
              <div style={{ fontSize: 12, color: "#aaa", marginBottom: 10, fontWeight: 500 }}>POST TO</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {PLATFORMS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => togglePlatform(p.id)}
                    style={{
                      padding: "7px 13px", borderRadius: 20,
                      border: `1.5px solid ${platforms.includes(p.id) ? p.color : "#e8e4dc"}`,
                      background: platforms.includes(p.id) ? `${p.color}18` : "white",
                      color: platforms.includes(p.id) ? p.color : "#aaa",
                      fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {p.name}
                  </button>
                ))}
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{ width: "100%", padding: 15, borderRadius: 14, border: "none", background: loading ? "#f0f0f0" : "linear-gradient(135deg,#FF6B35,#FFBE0B)", color: loading ? "#bbb" : "white", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}
              >
                {loading ? "Posting your review…" : "Submit Review ✦"}
              </button>
            </div>
          )}

          {/* ── STEP 2: Thank you ── */}
          {step === 2 && (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <div style={{ fontSize: 60, marginBottom: 14 }}>{isGated ? "💌" : "🎉"}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#111", marginBottom: 8 }}>
                {isGated ? "Feedback received!" : "Thank you!"}
              </div>
              <div style={{ fontSize: 14, color: "#999", lineHeight: 1.7, marginBottom: 20 }}>
                {isGated
                  ? "The manager will personally review your feedback and reach out soon."
                  : `Your review has been posted to ${platforms.length} platform${platforms.length !== 1 ? "s" : ""}. You're helping others!`}
              </div>

              {/* Incentive code */}
              {!isGated && business.hasIncentive && (
                <div style={{ background: "linear-gradient(135deg,#fff8f0,#fffaf0)", border: "1.5px solid #ffe5cc", borderRadius: 16, padding: "16px 20px", marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: "#FF6B35", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>🎁 THANK YOU GIFT</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: "#FF6B35", letterSpacing: "-0.02em", marginBottom: 4 }}>
                    {business.incentiveValue} OFF
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa" }}>
                    Use code <strong>{business.incentiveCode}</strong> on your next visit
                  </div>
                </div>
              )}

              {onBack && (
                <button
                  onClick={onBack}
                  style={{ padding: "11px 24px", borderRadius: 12, border: "1.5px solid #e8e4dc", background: "white", color: "#aaa", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
                >
                  ← Back to dashboard
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16, fontSize: 11, color: "#ccc" }}>Powered by JiffyReview.com</div>
    </div>
  );
}
